import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { useAction } from "../../../components/ui";
import { loadMahnleiter, loadSettingsWithDefaults, updateSettings, type SettingsPatch } from "./data";
import { BrandingForm, brandingDraftFrom, type BrandingTeil } from "./forms";
import { AbsenderPanel } from "./Vorlagen";
import type { SeiteKey } from "./baum";
import { amountsInHomeCurrency, listText } from "./model";
import { NurAdmin, Confirm, SettingsFrame } from "./parts";
import { t, ta } from "../../../lib/sprache";

/**
 * Einstellungen · Branding (11_app_agentur/einstellungen-branding.html). Seit IA-1 drei Seiten aus demselben Formular und
 * demselben Speicherweg (updateSettings): Branding (Logo, Agenturfarbe mit Kontrastprüfung, Portalname), Sprache & Region
 * (Sprache der Belege, Hauswährung, Währungen, Format) und Mailversand (Absender, Antwortadresse, Fußzeile; dazu Absender je
 * Zweck und die eigene Absenderdomain an EINEM Ort).
 */
export function Branding() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NurAdmin />;
  return (
    <BrandingSeite orgId={ws.org.org_id} orgName={ws.org.name} teile={["marke"]} seite="branding" title={t("Branding", "Branding")}
      text={t("Logo, Akzentfarbe und Portalname auf Rechnungen, Verträgen und in Mails.", "Logo, accent colour and portal name on invoices, contracts and emails.")}
      darunter={<p className="notice tk-verweise"><Link to="/agentur/einstellungen/mailversand">{t("Absender und Fußzeile der Mails: Mailversand", "Email sender and footer: Email sending")}</Link>{" · "}<Link to="/agentur/einstellungen/region">{t("Sprache, Währung und Format: Sprache & Region", "Language, currency and format: Language & region")}</Link></p>} />
  );
}

/** IA-1: Sprache der Belege, Sprachen, Hauswährung, weitere Währungen, Zahlen- und Datumsformat; Land und Zeitzone stehen beim Profil. */
export function Region() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NurAdmin />;
  return (
    <BrandingSeite orgId={ws.org.org_id} orgName={ws.org.name} teile={["region"]} seite="region" title={t("Sprache & Region", "Language & region")}
      text={t("Sprache der Belege, Währungen und Format von Zahlen und Datum.", "Document language, currencies and number and date format.")}
      darunter={<p className="notice tk-verweise">{ta("Land und Zeitzone der Agentur: ", "The agency's country and time zone: ", "Land und Zeitzone deines Kontos: ", "Your account's country and time zone: ")}<Link to="/agentur/einstellungen/firma#region">{t("Profil & Rechtsangaben", "Profile & legal details")}</Link>{t(". Deine eigene Sprache der Oberfläche wählst du im Profilmenü.", ". You choose your own interface language in the profile menu.")}</p>} />
  );
}

/** IA-1: EIN Ort für den Mailversand (Absender, Antwortadressen je Zweck, Signatur, eigene Absenderdomain, CreatorOffice-Hinweis). */
export function Mailversand() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NurAdmin />;
  return (
    <BrandingSeite orgId={ws.org.org_id} orgName={ws.org.name} teile={["absender"]} seite="mailversand" title={t("Mailversand & Absenderdomain", "Email sending & sender domain")}
      text={t("Absendername, Antwortadressen, Fußzeile und die eigene Absenderdomain für alle Mails.", "Sender name, reply addresses, footer and the custom sender domain for all emails.")}
      darunter={<AbsenderPanel orgId={ws.org.org_id} />} />
  );
}

function BrandingSeite({ orgId, orgName, teile, seite, title, text, darunter }: {
  orgId: string; orgName: string; teile: readonly BrandingTeil[]; seite: SeiteKey; title: string; text: string; darunter?: ReactNode;
}) {
  const q = useQuery({ queryKey: ["org_settings", orgId, "branding"], queryFn: () => loadSettingsWithDefaults(api, orgId) });
  const { busy, error, run } = useAction();
  const qc = useQueryClient();
  const [currencyChange, setCurrencyChange] = useState<{ values: SettingsPatch; from: string; to: string; amounts: string[] } | null>(null);
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const { settings, defaults } = q.data;

  const store = async (v: SettingsPatch) => {
    const ok = await run("branding", async () => {
      await updateSettings(api, orgId, v);
      // Farbe, Logo und Zahlenformat stehen im Rahmen des Portals (Kontodaten)
      await qc.invalidateQueries({ queryKey: ["account"] });
    }, t("Branding gespeichert.", "Branding saved."));
    if (ok) setCurrencyChange(null);
  };
  // Hauswährung wechseln: Festbeträge ohne eigene Währung gelten danach in der neuen Währung (Befund 10) – ausdrücklich bestätigen
  async function submit(v: SettingsPatch) {
    if (v.currency && v.currency !== settings.currency) {
      const m = await loadMahnleiter(api, orgId).catch(() => null);
      const amounts = amountsInHomeCurrency(settings, m?.policy ?? null, m?.steps ?? []);
      setCurrencyChange({ values: v, from: settings.currency, to: v.currency, amounts });
      return;
    }
    await store(v);
  }

  return (
    <SettingsFrame section="branding" seite={seite} orgId={orgId} title={title} text={text}>
      <BrandingForm
        // Praxistest K-082: Ein Logo-Upload ändert nur logo_path und updated_at. Hinge der Schlüssel an updated_at, setzte der
        // Upload das Formular zurück und verwarf ungespeicherte Eingaben. Neu aufbauen nur, wenn sich ein Feld des Formulars ändert.
        key={JSON.stringify({ ...settings, logo_path: undefined, updated_at: undefined })}
        initial={brandingDraftFrom(settings, defaults, { orgName, email: api.session()?.email ?? "" })}
        orgName={orgName}
        orgId={orgId}
        logoPath={settings.logo_path}
        strict={() => true}
        busy={!!busy}
        error={currencyChange ? null : error}
        buttons={<button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}</button>}
        onSubmit={(v) => void submit(v)}
        teile={teile}
      />
      {darunter}
      <Confirm open={!!currencyChange} title={t("Hauswährung wechseln?", "Change home currency?")} busy={busy === "branding"} confirmLabel={t(`Auf ${currencyChange?.to ?? ""} umstellen`, `Switch to ${currencyChange?.to ?? ""}`)}
        onClose={() => setCurrencyChange(null)} onConfirm={() => currencyChange && void store(currencyChange.values)}
        text={currencyChange && (
          <>
            {t("Die Hauswährung wechselt von ", "The home currency changes from ")}{currencyChange.from} {t("auf ", "to ")}{currencyChange.to}{t(". Gestellte Rechnungen und Kooperationen behalten ihre Währung.", ". Issued invoices and deals keep their currency.")}{currencyChange.amounts.length > 0
              ? t(` Diese Festbeträge haben keine eigene Währung und gelten danach als ${currencyChange.to}, mit derselben Zahl: ${listText(currencyChange.amounts)}. Prüfe sie unter Provision und Mahnwesen.`, ` These fixed amounts have no currency of their own and will then count as ${currencyChange.to}, with the same number: ${listText(currencyChange.amounts)}. Check them under Commission and Dunning.`)
              : t(" Festbeträge ohne eigene Währung sind nicht hinterlegt.", " No fixed amounts without their own currency are on file.")}
            {error && <span className="tk-error" role="alert"> {error}</span>}
          </>
        )} />
    </SettingsFrame>
  );
}
